import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';

/**
 * Firebase Storage Image Utilities
 * Handles loading images from Firebase Storage with proper caching and fallbacks
 */

// Cache for storing downloaded URLs to avoid repeated Firebase calls
const urlCache = new Map();

/**
 * Get download URL from Firebase Storage path
 * @param {string} storagePath - Path to the image in Firebase Storage (e.g., 'products/bounce-house.jpg')
 * @returns {Promise<string|null>} - Download URL or null if failed
 */
export const getFirebaseImageUrl = async (storagePath) => {
  if (!storagePath || typeof storagePath !== 'string') {
    return null;
  }

  // Check cache first
  if (urlCache.has(storagePath)) {
    const cachedValue = urlCache.get(storagePath);
    if (cachedValue) {
      console.log(`💾 Using cached URL for: ${storagePath}`);
    }
    return cachedValue;
  }

  try {
    // Create a reference to the file
    const imageRef = ref(storage, storagePath);
    
    // Get the download URL
    const downloadURL = await getDownloadURL(imageRef);
    
    // Cache the URL for future use (cache for 1 hour)
    urlCache.set(storagePath, downloadURL);
    
    // Auto-clear cache after 1 hour
    setTimeout(() => {
      urlCache.delete(storagePath);
    }, 3600000); // 1 hour in milliseconds
    
    console.log(`🔥 Successfully loaded from Firebase: ${storagePath}`);
    return downloadURL;
  } catch (error) {
    // Only show detailed error for debugging, not warnings
    if (error.code === 'storage/object-not-found') {
      // This is expected for most attempts, so don't spam console
      console.log(`📁 File not found: ${storagePath}`);
    } else {
      console.warn(`❗ Firebase Storage error for ${storagePath}:`, error.code, error.message);
    }
    
    // Cache the failure to avoid repeated attempts (shorter cache for failures)
    urlCache.set(storagePath, null);
    setTimeout(() => {
      urlCache.delete(storagePath);
    }, 300000); // Cache failures for 5 minutes
    
    return null;
  }
};

/**
 * Get Firebase image URL with multiple path attempts
 * Tries different common image paths and extensions
 * @param {string} baseName - Base name of the image (e.g., 'bounce-house')
 * @param {string} folder - Folder in Firebase Storage (default: 'products')
 * @returns {Promise<string|null>} - Download URL or null if all attempts failed
 */
export const getFirebaseImageUrlWithFallbacks = async (baseName, folder = 'products') => {
  if (!baseName) return null;

  console.log(`🔍 Searching for image: "${baseName}"`);

  // Clean the base name
  const cleanBaseName = baseName.toLowerCase()
    .replace(/[^a-z0-9-\s]/g, '') // Remove special characters except hyphens
    .replace(/\s+/g, '-')         // Replace spaces with hyphens
    .replace(/-+/g, '-')          // Replace multiple hyphens with single
    .trim();

  // Common image extensions to try
  const extensions = ['.jpg', '.jpeg', '.png', '.webp', '.gif'];
  
  // More comprehensive naming patterns to try
  const nameVariations = [
    cleanBaseName,
    cleanBaseName.replace(/-/g, ''),     // No hyphens: "dinokingdomduallanecombo"
    cleanBaseName.replace(/-/g, '_'),    // Underscores: "dino_kingdom_dual_lane_combo"
    cleanBaseName.replace(/-/g, ' '),    // Spaces: "dino kingdom dual lane combo"
    
    // Try shortened versions by taking first few words
    cleanBaseName.split('-').slice(0, 2).join('-'), // "dino-kingdom"
    cleanBaseName.split('-').slice(0, 3).join('-'), // "dino-kingdom-dual"
    cleanBaseName.split('-')[0], // Just "dino"
    
    // Try removing common words
    cleanBaseName.replace(/-combo$/, ''),     // Remove "combo" at end
    cleanBaseName.replace(/-dual-lane/, ''),  // Remove "dual-lane"
    cleanBaseName.replace(/-lane/, ''),       // Remove "lane"
    cleanBaseName.replace(/^dual-/, ''),      // Remove "dual" at start
    
    // Try acronyms and abbreviations
    cleanBaseName.split('-').map(word => word[0]).join(''), // "dkdlc"
    
    // Original variations
    baseName.toLowerCase().replace(/\s+/g, '-'),
    baseName.toLowerCase().replace(/\s+/g, ''),
    baseName.toLowerCase().replace(/\s+/g, '_'),
    baseName // Original name as final fallback
  ];

  // Remove duplicates and empty strings
  const uniqueVariations = [...new Set(nameVariations)].filter(name => name && name.length > 0);

  console.log(`📝 Trying ${uniqueVariations.length} name variations:`, uniqueVariations);

  // Try each combination
  for (const nameVariation of uniqueVariations) {
    for (const ext of extensions) {
      const storagePath = `${folder}/${nameVariation}${ext}`;
      console.log(`🔎 Checking: ${storagePath}`);
      
      const url = await getFirebaseImageUrl(storagePath);
      if (url) {
        console.log(`✅ Found image at: ${storagePath}`);
        return url;
      }
    }
  }

  console.log(`❌ No image found for: "${baseName}"`);
  return null;
};

/**
 * Smart image URL resolver
 * Handles different input formats and tries to get the best image URL
 * @param {string|object} imageSource - Can be a URL, Firebase path, or object with image properties
 * @param {string} fallbackName - Product name to use for fallback image search
 * @returns {Promise<string|null>} - Best available image URL
 */
export const resolveImageUrl = async (imageSource, fallbackName = '') => {
  // If already a complete URL (http/https), return as-is
  if (typeof imageSource === 'string' && (imageSource.startsWith('http://') || imageSource.startsWith('https://'))) {
    return imageSource;
  }

  // If it's a Firebase Storage path
  if (typeof imageSource === 'string' && imageSource.includes('/')) {
    const url = await getFirebaseImageUrl(imageSource);
    if (url) return url;
  }

  // If it's just a filename, try to find it in Firebase Storage
  if (typeof imageSource === 'string' && imageSource.length > 0) {
    const url = await getFirebaseImageUrlWithFallbacks(imageSource);
    if (url) return url;
  }

  // If it's an object, extract relevant properties
  if (typeof imageSource === 'object' && imageSource !== null) {
    const possiblePaths = [
      imageSource.url,
      imageSource.imageUrl,
      imageSource.mainImage,
      imageSource.image,
      imageSource.src
    ];

    for (const path of possiblePaths) {
      if (path) {
        const url = await resolveImageUrl(path, fallbackName);
        if (url) return url;
      }
    }
  }

  // Last resort: try using the fallback name
  if (fallbackName) {
    const url = await getFirebaseImageUrlWithFallbacks(fallbackName);
    if (url) return url;
  }

  return null;
};

/**
 * Preload images for better performance
 * @param {Array} imageSources - Array of image sources to preload
 * @param {string} fallbackName - Fallback name for image search
 */
export const preloadFirebaseImages = async (imageSources, fallbackName = '') => {
  const preloadPromises = imageSources.map(async (imageSource) => {
    const url = await resolveImageUrl(imageSource, fallbackName);
    if (url) {
      // Create image element to trigger browser preload
      const img = new Image();
      img.src = url;
    }
  });

  await Promise.all(preloadPromises);
};

/**
 * Clear the URL cache (useful for testing or memory management)
 */
export const clearImageCache = () => {
  urlCache.clear();
};

/**
 * Get cache size (for debugging)
 */
export const getCacheSize = () => {
  return urlCache.size;
};